import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { Button, Select, Table, type TableProps } from 'antd';

import { modalMethod } from 'components';
import { useCommonCodeOptions, useDownloadExcelFile, useExcelStorage, useMeInfo } from 'hooks';
import { ExcelStorageItem } from 'interfaces/store';
import { TableWrapper } from 'pages/SalesSettlement/styles';
import { SearchFilterBar, TableCard } from './styles';

const EXCEL_CONTENT_TYPE_CATEGORY = 'STORE_EXCEL_LOG_CONTENT_TYPE';
const PAGE_SIZE = 50;
const DOWNLOAD_REFRESH_DELAY_MS = 1000;

// 연도 옵션: 현재 연도부터 -5년까지 (최근 6년, 매년 자동 갱신). 기본 선택값은 현재 연도
const RECENT_YEAR_COUNT = 6;
const SEARCH_YEAR_OPTIONS = Array.from({ length: RECENT_YEAR_COUNT }).map((_, index) => {
  const year = moment().year() - index;
  return { label: `${year}년`, value: year };
});

const formatDateTime = (value?: string | null) => (value ? moment(value).format('YYYY. M. DD. / HH:mm:ss') : '-');
const formatDate = (value?: string | null) => (value ? moment(value).format('YYYY. M. DD.') : '-');

const ExcelStorage = () => {
  const { meInfo } = useMeInfo();
  const [page, setPage] = useState<number>(1);
  const [year, setYear] = useState<number>(moment().year());
  const [sid, setSid] = useState<string | undefined>(undefined);
  const [contentType, setContentType] = useState<string | undefined>(undefined);

  // 요청자료 필터 옵션 — 공통코드(STORE_EXCEL_LOG_CONTENT_TYPE) (보관함 진입 시 1회 조회 후 세션 캐시)
  const { options: contentTypeOptions } = useCommonCodeOptions(EXCEL_CONTENT_TYPE_CATEGORY);

  // 요청 제휴점 필터 옵션: 로그인 계정에 연결된 제휴점 목록 (제휴점 전환 스위처와 동일 소스)
  const storeOptions = useMemo(
    () => (meInfo?.storeList ?? []).map(({ sid: storeSid, name }) => ({ label: name, value: storeSid })),
    [meInfo?.storeList]
  );

  const { data, refetch, isLoading, isError } = useExcelStorage({ page, year, sid, contentType });
  const { mutateAsync: requestExcelDownload } = useDownloadExcelFile();

  const downloadExcel = async (excel: ExcelStorageItem) => {
    const fileName = `${excel.contentTypeName}_${moment(excel.requestDate).format('YYYY-MM-DD_HH:mm:ss')}`;
    try {
      const blob = await requestExcelDownload({ fileId: excel.fileId, sid: excel.sid, fileName });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${fileName}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      // 다운로드 횟수/상태 갱신
      setTimeout(() => {
        refetch();
      }, DOWNLOAD_REFRESH_DELAY_MS);
    } catch {
      modalMethod.error('다운로드에 실패했습니다.');
    }
  };

  // 필터 변경 시 1페이지로 리셋
  const handleFilterChange =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setPage(1);
      setter(value);
    };

  const columns: TableProps<ExcelStorageItem>['columns'] = [
    { title: '요청자', dataIndex: 'storeAccountName', align: 'center', render: (value) => value || '-' },
    { title: '요청 제휴점', dataIndex: 'storeName', align: 'center', render: (value) => value || '-' },
    { title: '요청일', dataIndex: 'requestDate', align: 'center', render: (value) => formatDateTime(value) },
    { title: '생성일', dataIndex: 'makeDate', align: 'center', render: (value) => formatDateTime(value) },
    { title: '요청자료', dataIndex: 'contentTypeName', align: 'center', render: (value) => value || '-' },
    { title: '다운로드 만료일', dataIndex: 'expireDate', align: 'center', render: (value) => formatDate(value) },
    { title: '다운로드횟수', dataIndex: 'downCount', align: 'center', render: (value) => value ?? 0 },
    {
      title: '상태',
      dataIndex: 'status',
      align: 'center',
      render: (status: number, record) =>
        status === 5 ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => downloadExcel(record)}>
            다운로드
          </Button>
        ) : (
          record.statusName || '-'
        )
    }
  ];

  return (
    <div>
      <SearchFilterBar>
        <span style={{ fontSize: '16px', fontWeight: 700 }}>검색조건</span>
        <Select value={year} options={SEARCH_YEAR_OPTIONS} onChange={handleFilterChange(setYear)} />
        <Select
          allowClear
          placeholder="요청 제휴점 명"
          value={sid}
          options={storeOptions}
          onChange={handleFilterChange(setSid)}
        />
        <Select
          allowClear
          placeholder="요청자료"
          value={contentType}
          options={contentTypeOptions}
          onChange={handleFilterChange(setContentType)}
        />
      </SearchFilterBar>

      <TableCard>
        <TableWrapper>
          <Table
            rowKey="fileId"
            columns={columns}
            dataSource={data?.content || []}
            loading={isLoading}
            locale={{ emptyText: isError ? '목록을 불러오지 못했습니다.' : '내역이 없습니다' }}
            pagination={{ total: data?.totalCount || 0, pageSize: PAGE_SIZE, current: page, showSizeChanger: false }}
            onChange={(pagination) => setPage(pagination.current || 1)}
          />
        </TableWrapper>
      </TableCard>
    </div>
  );
};

export default ExcelStorage;
